import { supabase } from './supabaseClient';

/**
 * Upload an image file to Supabase Storage
 * @param {File} file - The image file to upload
 * @param {string} folder - Optional folder path (e.g., 'recipes', 'profiles')
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadImage = async (file, folder = 'recipes') => {
  try {
    // Validate file
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File must be an image' };
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('recipe-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      return { success: false, error: error.message };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('recipe-images')
      .getPublicUrl(fileName);

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Upload error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete an image from Supabase Storage
 * @param {string} imageUrl - The full URL of the image to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    if (!imageUrl || !imageUrl.includes('recipe-images')) {
      return { success: false, error: 'Invalid image URL' };
    }

    // Extract file path from URL
    const urlParts = imageUrl.split('/recipe-images/');
    if (urlParts.length < 2) {
      return { success: false, error: 'Could not parse image URL' };
    }
    
    const filePath = urlParts[1];

    // Delete from storage
    const { error } = await supabase.storage
      .from('recipe-images')
      .remove([filePath]);

    if (error) {
      console.error('Delete error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Compress and resize an image before upload
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>}
 */
export const compressImage = (file, maxWidth = 1000, maxHeight = 1000, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    // Validate file is actually a File/Blob
    if (!(file instanceof File) && !(file instanceof Blob)) {
      reject(new Error('Invalid file object'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let { width, height } = img;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            quality
          );
        } catch (error) {
          reject(new Error('Canvas error: ' + error.message));
        }
      };
      
      img.onerror = (error) => {
        console.error('Image load error:', error);
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Upload an image with automatic compression
 * @param {File} file - The image file to upload
 * @param {string} folder - Optional folder path
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadImageWithCompression = async (file, folder = 'recipes') => {
  try {
    // Compress the image first
    const compressedBlob = await compressImage(file);
    
    // Convert blob to file
    const compressedFile = new File([compressedBlob], file.name, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
    
    // Upload the compressed image
    return await uploadImage(compressedFile, folder);
  } catch (error) {
    console.error('Compression error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Upload forum post image to Supabase Storage
 * Optimized for community forum with smart compression
 * @param {File} file - The image file to upload
 * @param {string} userId - User ID for organizing uploads
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadForumImage = async (file, userId = 'anonymous') => {
  try {
    // Validate file
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    console.log('File to upload:', file.name, file.type, file.size);

    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File must be an image' };
    }

    // Block HEIC files - browsers can't display them
    if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
      return { 
        success: false, 
        error: 'HEIC format not supported. Please convert to JPG first: Open the photo, tap Share, and select "Save as JPG" or use a JPG/PNG photo instead.' 
      };
    }

    let uploadFile;
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    
    // Try compression, fall back to original if it fails
    try {
      console.log('Attempting compression...');
      const compressedBlob = await compressImage(file, 1000, 1000, 0.75);
      console.log('Compression successful! Original:', file.size, 'Compressed:', compressedBlob.size);
      
      // Always save as JPG after compression
      const fileName = `${userId}/${timestamp}-${randomId}.jpg`;
      uploadFile = new File([compressedBlob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
    } catch (compressionError) {
      console.warn('Compression failed, uploading original:', compressionError.message);
      // Use original file if compression fails
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `${userId}/${timestamp}-${randomId}.${fileExt}`;
      uploadFile = new File([file], fileName, {
        type: file.type,
        lastModified: Date.now()
      });
    }
    
    const fileName = uploadFile.name;

    // Upload to forum-images bucket
    const { data, error } = await supabase.storage
      .from('forum-images')
      .upload(fileName, uploadFile, {
        cacheControl: '31536000', // Cache for 1 year
        upsert: false
      });

    if (error) {
      console.error('Forum image upload error:', error);
      return { success: false, error: error.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('forum-images')
      .getPublicUrl(fileName);

    const publicUrl = urlData?.publicUrl;
    
    console.log('Upload successful! URL:', publicUrl);

    return { success: true, url: publicUrl, path: fileName };
  } catch (error) {
    console.error('Forum image upload error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a forum image from Supabase Storage
 * @param {string} imageUrl - The full URL or path of the image
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteForumImage = async (imageUrl) => {
  try {
    if (!imageUrl) {
      return { success: false, error: 'No image URL provided' };
    }

    // Extract file path from URL
    let filePath = imageUrl;
    if (imageUrl.includes('forum-images/')) {
      const urlParts = imageUrl.split('/forum-images/');
      filePath = urlParts[urlParts.length - 1];
    }

    // Delete from storage
    const { error } = await supabase.storage
      .from('forum-images')
      .remove([filePath]);

    if (error) {
      console.error('Delete forum image error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete forum image error:', error);
    return { success: false, error: error.message };
  }
};
